import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { format } from 'date-fns';
import {
  AlertTriangle, Clock, LogOut, PhoneOff, Shield, TrendingDown,
  ChevronDown, ChevronUp, Info, Award, AlertCircle, CheckCircle
} from 'lucide-react';
import {
  THRESHOLDS, POINT_TYPES,
  getActivePoints, calculateTotalPoints, getWarningLevel,
  getNextThreshold, formatPoints
} from '../utils/attendancePoints';
import './EmployeeAttendanceView.css';

function EmployeeAttendanceView({ employeeId }) {
  const { getEmployeeAttendancePoints, getEmploymentStatus } = useApp();
  const [showHistory, setShowHistory] = useState(false);

  const points = getEmployeeAttendancePoints(employeeId);
  const activePoints = getActivePoints(points);
  const totalPoints = calculateTotalPoints(points);
  const warningLevel = getWarningLevel(totalPoints);
  const nextThreshold = getNextThreshold(totalPoints);
  const employment = getEmploymentStatus(employeeId);

  const infractions = activePoints.filter(p => p.points > 0 && !p.voided);
  const reductions = activePoints.filter(p => p.points < 0 && !p.voided);

  const statusColor = totalPoints >= 8 ? '#991b1b'
    : totalPoints >= 5 ? '#ef4444'
    : totalPoints >= 3 ? '#f59e0b'
    : '#10b981';

  const statusLabel = totalPoints >= 8 ? 'Critical'
    : totalPoints >= 5 ? 'Warning'
    : totalPoints >= 3 ? 'Caution'
    : 'Good Standing';

  const typeIcon = (type) => {
    switch (type) {
      case 'tardiness': return <Clock size={14} />;
      case 'early_leave': return <LogOut size={14} />;
      case 'callout': return <PhoneOff size={14} />;
      case 'no_call_no_show': return <AlertTriangle size={14} />;
      case 'reduction': return <TrendingDown size={14} />;
      case 'manager_override': return <Shield size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  return (
    <div className="emp-att-view">
      {/* Main Status Card */}
      <div className="emp-att-status-card">
        <div className="emp-att-status-header">
          <h3>My Attendance</h3>
          <div className="emp-att-status-badge" style={{ background: statusColor + '18', color: statusColor }}>
            {statusLabel}
          </div>
        </div>

        {/* Big Points Display */}
        <div className="emp-att-points-display">
          <div className="emp-att-big-number" style={{ color: statusColor }}>
            {totalPoints.toFixed(2)}
          </div>
          <div className="emp-att-points-label">points (90-day rolling)</div>
        </div>

        {/* Progress toward thresholds */}
        <div className="emp-att-progress">
          <div className="emp-att-progress-bar">
            <div
              className="emp-att-progress-fill"
              style={{
                width: `${Math.min((totalPoints / 8) * 100, 100)}%`,
                background: statusColor
              }}
            />
            {THRESHOLDS.map(t => (
              <div
                key={t.level}
                className="emp-att-marker"
                style={{ left: `${(t.points / 8) * 100}%` }}
              >
                <div className="emp-att-marker-line" style={{ background: t.color }} />
                <span className="emp-att-marker-label">{t.points}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Threshold Legend */}
        <div className="emp-att-legend">
          {THRESHOLDS.map(t => (
            <div
              key={t.level}
              className={`emp-att-legend-item ${totalPoints >= t.points ? 'reached' : ''}`}
            >
              <span className="emp-att-legend-dot" style={{ background: t.color }} />
              <span className="emp-att-legend-text">{t.points}pts = {t.label}</span>
            </div>
          ))}
        </div>

        {/* Next threshold warning */}
        {nextThreshold && totalPoints > 0 && (
          <div className="emp-att-next-warning">
            <AlertCircle size={14} />
            <span>
              You are <strong>{nextThreshold.pointsAway} point{nextThreshold.pointsAway !== 1 ? 's' : ''}</strong> away from <strong>{nextThreshold.label}</strong>
            </span>
          </div>
        )}

        {totalPoints === 0 && (
          <div className="emp-att-perfect">
            <Award size={18} />
            <span>Perfect attendance! Keep it up!</span>
          </div>
        )}
      </div>

      {/* Employment & PTO Status */}
      <div className="emp-att-pto-card">
        <div className="emp-att-pto-row">
          <span className="emp-att-pto-label">Employment Status</span>
          <span className={`emp-att-pto-value ${employment.status}`}>
            {employment.status === 'full_time' ? 'Full-Time' : 'Part-Time'}
          </span>
        </div>
        <div className="emp-att-pto-row">
          <span className="emp-att-pto-label">Avg Weekly Hours (8wk)</span>
          <span className="emp-att-pto-value">{employment.avgWeeklyHours} hrs</span>
        </div>
        <div className="emp-att-pto-row">
          <span className="emp-att-pto-label">Full-Time Threshold</span>
          <span className="emp-att-pto-value">{employment.threshold} hrs/week</span>
        </div>
        <div className="emp-att-pto-row">
          <span className="emp-att-pto-label">PTO Eligible</span>
          <span className={`emp-att-pto-value ${employment.ptoEligible ? 'yes' : 'no'}`}>
            {employment.ptoEligible ? (
              <><CheckCircle size={14} /> Yes</>
            ) : (
              <><AlertTriangle size={14} /> No (below {employment.threshold} hrs/wk)</>
            )}
          </span>
        </div>
      </div>

      {/* Quick Summary */}
      <div className="emp-att-summary-row">
        <div className="emp-att-summary-item">
          <span className="emp-att-summary-num" style={{ color: infractions.length > 0 ? '#ef4444' : '#10b981' }}>{infractions.length}</span>
          <span className="emp-att-summary-label">Infractions</span>
        </div>
        <div className="emp-att-summary-item">
          <span className="emp-att-summary-num" style={{ color: '#10b981' }}>{reductions.length}</span>
          <span className="emp-att-summary-label">Reductions</span>
        </div>
      </div>

      {/* Point History Toggle */}
      <button className="emp-att-history-toggle" onClick={() => setShowHistory(!showHistory)}>
        {showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        {showHistory ? 'Hide' : 'View'} Point History ({activePoints.length})
      </button>

      {showHistory && (
        <div className="emp-att-history">
          {activePoints.length === 0 ? (
            <div className="emp-att-history-empty">
              <Award size={24} />
              <p>No attendance points in the last 90 days!</p>
            </div>
          ) : (
            <div className="emp-att-history-list">
              {activePoints
                .filter(p => !p.voided)
                .sort((a, b) => (b.date || b.createdAt).localeCompare(a.date || a.createdAt))
                .map(p => (
                  <div key={p.id} className={`emp-att-entry ${p.points < 0 ? 'reduction' : ''}`}>
                    <div className="emp-att-entry-icon" style={{ color: POINT_TYPES[p.type]?.color || '#6b7280' }}>
                      {typeIcon(p.type)}
                    </div>
                    <div className="emp-att-entry-info">
                      <div className="emp-att-entry-desc">
                        {p.description || POINT_TYPES[p.type]?.label || p.type}
                      </div>
                      <div className="emp-att-entry-date">
                        {p.date && format(new Date(p.date), 'MMM d, yyyy')}
                        {p.scheduledTime && ` | Shift: ${p.scheduledTime}`}
                        {p.actualTime && ` | Actual: ${format(new Date(p.actualTime), 'h:mm a')}`}
                      </div>
                      {p.managerNote && (
                        <div className="emp-att-entry-note">
                          <Info size={11} /> {p.managerNote}
                        </div>
                      )}
                    </div>
                    <div className={`emp-att-entry-pts ${p.points < 0 ? 'good' : 'bad'}`}>
                      {formatPoints(p.points)}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* How points work info */}
          <div className="emp-att-info-box">
            <h4><Info size={14} /> How Points Work</h4>
            <ul>
              <li>Points are based on a <strong>90-day rolling window</strong> and expire automatically.</li>
              <li><strong>Tardiness:</strong> 1-6 min = 0.25 pts, 7-30 min = 0.75, 31-59 min = 1.25, 60+ min = 2.0</li>
              <li><strong>Leaving Early:</strong> Only logged manually by a manager (0 pts if manager-approved)</li>
              <li><strong>Call Outs:</strong> Points depend on notice given and PTO usage</li>
              <li><strong>No Call / No Show:</strong> 7 points</li>
              <li><strong>Perfect Attendance:</strong> 30 days = -0.5 pts, 60 days = -1.0 pts</li>
              <li>PTO = paid time off, and does NOT remove points entirely</li>
              <li>Coverage by another employee does NOT remove your points</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployeeAttendanceView;
